import { useContext, useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Table, Badge, Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import AnnualReviewService from '../../../services/AnnualReview';
import { AnnualReviewResponse, AnnualReviewStatus } from './types';

const statusBadgeColor = (status: AnnualReviewStatus): string => {
  switch (status) {
    case 'FINISHED':
      return 'success';
    case 'CANCELED':
      return 'secondary';
    case 'AWAITING_SIGNATURES':
    case 'AWAITING_GROWTH_PLAN':
      return 'warning';
    default:
      return 'info';
  }
};

const statusLabel = (status: AnnualReviewStatus): string => {
  switch (status) {
    case 'PENDING_BOTH':
      return 'Pending Both';
    case 'PENDING_EMPLOYEE':
      return 'Awaiting Employee';
    case 'PENDING_SUPERVISOR':
      return 'Awaiting Supervisor';
    case 'AWAITING_GROWTH_PLAN':
      return 'Awaiting Growth Plan';
    case 'AWAITING_SIGNATURES':
      return 'Awaiting Signatures';
    case 'FINISHED':
      return 'Completed';
    case 'CANCELED':
      return 'Canceled';
    default:
      return status;
  }
};

interface AnnualReviewsSectionProps {
  userId: string;
}

const AnnualReviewsSection = ({ userId }: AnnualReviewsSectionProps) => {
  const { token }: any = useContext(AuthContext);
  const history = useHistory();
  const [reviews, setReviews] = useState<AnnualReviewResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await AnnualReviewService(token).listByUserId(userId);
        setReviews(res?.data || []);
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Could not load annual reviews');
      } finally {
        setLoading(false);
      }
    };
    if (userId && token) load();
  }, [userId, token]);

  return (
    <Card className="mt-3">
      <CardHeader>
        <h4 className="mb-0">
          <i className="fas fa-clipboard-list mr-2" /> Annual Reviews
        </h4>
      </CardHeader>
      <CardBody>
        {loading ? (
          <p className="text-muted m-0">Loading…</p>
        ) : !reviews.length ? (
          <p className="text-muted m-0">No annual reviews yet.</p>
        ) : (
          <Table responsive striped className="m-0">
            <thead>
              <tr>
                <th>Cycle</th>
                <th>Employee</th>
                <th>Supervisor</th>
                <th>Status</th>
                <th>Last update</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id}>
                  <td>{r.cycleYear}</td>
                  <td>{r.employeeCompletedAt ? moment(r.employeeCompletedAt).format('MM/DD/YY') : '—'}</td>
                  <td>{r.supervisorCompletedAt ? moment(r.supervisorCompletedAt).format('MM/DD/YY') : '—'}</td>
                  <td>
                    <Badge color={statusBadgeColor(r.status)} pill>
                      {statusLabel(r.status)}
                    </Badge>
                  </td>
                  <td>{moment(r.updatedAt).format('MM/DD/YY')}</td>
                  <td className="text-right">
                    {r.status === 'FINISHED' && r.finalSignedPdfUrl ? (
                      <a
                        className="btn btn-sm btn-outline-primary"
                        href={r.finalSignedPdfUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <i className="fas fa-download" /> Download
                      </a>
                    ) : r.status !== 'CANCELED' ? (
                      <Button color="primary" size="sm" onClick={() => history.push(`/staff/annual-reviews/${r.id}`)}>
                        Open
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

export default AnnualReviewsSection;
